import { Node } from '@policygenius/dif-ts-types';
import {
  DatapointvaluesDPIDValueItem,
  InterviewdataGetResponse,
  InterviewdataNextInterviewSectionURLResponse,
  SequentialinterviewsPostResponse,
} from 'api';
import formatResponseError from 'api/util/formatResponseError';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { addGlobalError } from 'containers/App/actions';

import useApi, { Error } from '../useApi';
import { buildBodyFromInterview } from '../useDataPointValues';
import {
  UseCreateInterviewSectionDataArgs,
  UseFetchInterviewNextSectionArgs,
  UseFetchInterviewSectionAndDataArgs,
} from './types';

export function useFetchInterviewNextSection({
  lazy = false,
  params,
  onCompleted,
  onError,
}: UseFetchInterviewNextSectionArgs) {
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, data, loading, error, activeUserUuid, referenceID }] =
    useApi<InterviewdataNextInterviewSectionURLResponse>({
      onCompleted,
      onError: onError ?? defaultOnError,
      initialLoading: !lazy,
    });

  const fetchInterviewNextSection = async (): Promise<{
    data: InterviewdataNextInterviewSectionURLResponse | null;
    error: Error | null;
  }> => {
    const { interviewURN, sectionURN } = params;

    updateRequest({ status: 'initiate' });

    try {
      const { data: responseData } = await api.interviewNextSection.interviewNextSectionCreate(
        activeUserUuid,
        {
          interviewURN,
          referenceID,
          sectionURN,
        }
      );

      updateRequest({ status: 'complete', payload: responseData });

      return {
        data: responseData,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  useEffect(() => {
    if (!lazy) {
      fetchInterviewNextSection();
    }
  }, []);

  return {
    data,
    loading,
    error,
    fetchInterviewNextSection,
  };
}

export function useFetchInterviewSectionAndData({
  lazy = false,
  params,
  onCompleted,
  onError,
}: UseFetchInterviewSectionAndDataArgs) {
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, data, loading, error, activeUserUuid, referenceID }] =
    useApi<InterviewdataGetResponse>({
      onCompleted,
      onError: onError ?? defaultOnError,
      initialLoading: !lazy,
    });

  const fetchInterviewSectionAndData = async (): Promise<{
    data: InterviewdataGetResponse | null;
    error: Error | null;
  }> => {
    const { interviewURN, sectionURN } = params;

    updateRequest({ status: 'initiate' });

    try {
      const { data: responseData } =
        await api.interviewSectionAndData.interviewSectionAndDataDetail(activeUserUuid, {
          interviewURN,
          referenceID,
          sectionURN,
        });

      updateRequest({ status: 'complete', payload: responseData });

      return {
        data: responseData,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  useEffect(() => {
    if (!lazy) {
      fetchInterviewSectionAndData();
    }
  }, []);

  return {
    data,
    loading,
    error,
    fetchInterviewSectionAndData,
  };
}

export function useCreateInterviewSectionData({
  params,
  onCompleted,
  onError,
}: UseCreateInterviewSectionDataArgs) {
  const { interviewURN, sectionURN } = params;
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, loading, error, activeUserUuid, referenceID }] =
    useApi<SequentialinterviewsPostResponse>({
      onCompleted,
      onError: onError ?? defaultOnError,
    });

  const createInterviewData = async (
    dataPointValues: DatapointvaluesDPIDValueItem[]
  ): Promise<{
    data: SequentialinterviewsPostResponse | null;
    error: Error | null;
  }> => {
    updateRequest({ status: 'initiate' });

    try {
      const { data: responseData } =
        await api.interviewSectionAndData.interviewSectionAndDataCreate(activeUserUuid, {
          dataPointValues,
          referenceID,
          interviewURN,
          sectionURN,
        });

      updateRequest({ status: 'complete', payload: responseData });

      return {
        data: responseData,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  async function createInterviewDataFromSchema(
    values: Record<string, unknown>,
    schema: Node
  ): Promise<{
    data: SequentialinterviewsPostResponse | null;
    error: Error | null;
  }> {
    const dataPointValues = buildBodyFromInterview(values, schema);

    return createInterviewData(dataPointValues);
  }

  return {
    loading,
    error,
    createInterviewData,
    createInterviewDataFromSchema,
  };
}
